import { ICarDetails, IProductVariantKeyFeature } from '../../../../common/model/ICarDetails';
import { IPrices } from '../../../../common/model/IPrice';
import {
  CreateDealFromCarId_createDealFromCarId,
  CreateDealFromCarId_createDealFromCarId_car_disclaimers,
  CreateDealFromCarId_createDealFromCarId_car_images,
  CreateDealFromCarId_createDealFromCarId_car_prices,
  CreateDealFromCarId_createDealFromCarId_car_selectedFeatures,
} from '../graphql/mutations/__generated__/CreateDealFromCarId';
import {
  GetCustomizeCar_customizeCar,
  GetCustomizeCar_customizeCar_car_keyFeatures,
} from '../graphql/queries/__generated__/GetCustomizeCar';
import ICarConfigOption from '../../../../common/model/ICarConfigOption';
import IProductImage from '../../../../common/model/IProductImage';
import { GetCarSpecifications_carSpecifications } from '../graphql/queries/__generated__/GetCarSpecifications';
import { filterObject } from '../../gqlCommon/utils/utils';

export enum NewPriceType {
  FINAL_PRICE = 'FinalPrice',
  NET_PRICE = 'NetPrice',
}

enum OldPriceType {
  RDP = 'RDP',
  MLP = 'MLP',
  TOTAL_BALANCE = 'TotalBalance',
}

const keyFeaturesTransformer = (
  features: GetCustomizeCar_customizeCar_car_keyFeatures[],
): IProductVariantKeyFeature[] => {
  if (!features.length || !features) return [];

  return features.map((feature, index) => ({
    description: feature?.shortname ?? '',
    disclaimer: feature?.attributes?.disclaimer,
    order: feature?.attributes?.displayorder ?? index,
  }));
};

const mainPricesTransformer = (prices: CreateDealFromCarId_createDealFromCarId_car_prices): IPrices => {
  if (!prices) return [];

  const originalPrice = prices.carPrice.originalPrice;

  return [
    {
      type: OldPriceType.MLP,
      basePrice: originalPrice.find(price => price.type === NewPriceType.NET_PRICE).money.amount,
      currency: originalPrice.find(price => price.type === NewPriceType.NET_PRICE).money.currency,
    },
    {
      type: OldPriceType.RDP,
      basePrice: originalPrice.find(price => price.type === NewPriceType.FINAL_PRICE).money.amount,
      currency: originalPrice.find(price => price.type === NewPriceType.FINAL_PRICE).money.currency,
    },
    {
      type: OldPriceType.TOTAL_BALANCE,
      basePrice: prices.totalPrice.find(price => price.type === NewPriceType.FINAL_PRICE).money.amount,
      currency: prices.totalPrice.find(price => price.type === NewPriceType.FINAL_PRICE).money.currency,
    },
  ];
};

const priceTransformer = (prices: any[]): IPrices => {
  return prices.map(price => ({
    type: price.type,
    basePrice: price.money.amount,
    currency: price.money.currency,
  }));
};

const optionsTransformer = (
  features: CreateDealFromCarId_createDealFromCarId_car_selectedFeatures[],
): ICarConfigOption[] => {
  if (!features || !features.length) return [];

  return features.map(feature => ({
    id: `${feature?.id}`,
    description: null,
    title: feature?.shortname,
    category: {
      category: feature?.categoryCode,
      order: null,
    },
    prices: priceTransformer(feature?.prices),
    images: !feature?.image
      ? null
      : {
          type: feature?.image?.[0]?.type ?? null,
          url: feature?.image?.[0]?.url ?? null,
        },
  }));
};

const imageTransformer = (
  image: CreateDealFromCarId_createDealFromCarId_car_images[] | CreateDealFromCarId_createDealFromCarId_car_images,
  imageType?: string[],
): IProductImage[] => {
  if (!image) return [];

  if (typeof image === 'object' && !Array.isArray(image)) {
    return [{ type: image.type, url: image.url }];
  }

  return image.filter(({ type }) => imageType.includes(type)).map(({ type, url }) => ({ url, type }));
};

const generalDisclaimerTransformer = (
  disclaimers: CreateDealFromCarId_createDealFromCarId_car_disclaimers[],
): ICarDetails['disclaimers'] => {
  return { general: disclaimers?.map(disclaimer => disclaimer.value) || [], colour: '' };
};

const standardFeaturesTransformer = (
  specifications: GetCarSpecifications_carSpecifications[],
): { [key: string]: string[] } => {
  return specifications.reduce((acc: { [key: string]: string[] }, curr) => {
    if (curr.groupName === 'Feature') {
      acc[curr.name] = filterObject(JSON.parse(JSON.stringify(curr.specItems)), '__typename');
    }
    return acc;
  }, {} as { [key: string]: string[] });
};

const carConfigurationService = {
  createCarConfiguration: (
    dealData: CreateDealFromCarId_createDealFromCarId,
    selectedCar: GetCustomizeCar_customizeCar,
    carSpecifications: GetCarSpecifications_carSpecifications[],
  ): ICarDetails => {
    return {
      market: dealData.country,
      country: dealData.country,
      brand: dealData.brand,
      language: dealData.language,
      region: dealData.region,
      currency: dealData.currency,
      title: dealData.car?.displayName,
      externalId: null,
      detailsOptionsAggregated: dealData.car?.aggregatedOptions,
      bodyStyle: selectedCar.car?.bodyStyle?.attributes?.shortname,
      modelYear: +dealData.car?.nameplate?.attributes?.modelyear,
      nvicCode: selectedCar.car?.specPack?.attributes?.nviccode,
      fuel: dealData.car?.engine?.attributes?.fuelLabel,
      detailsAggregated: null,
      marketingCategory: selectedCar.car?.trim?.attributes?.marketingprefix,
      stock: {
        count: selectedCar?.similarStockCars?.length ?? 0,
        dms: selectedCar?.stockInfo?.dms ?? null,
        vin: selectedCar?.stockInfo?.vin ?? null,
      },
      discounts: dealData.car?.prices?.carPrice?.offers?.discounts.map(discount =>
        filterObject(JSON.parse(JSON.stringify(discount)), '__typename'),
      ),
      totalDiscount: dealData.car?.prices?.carPrice?.totalDiscount.map(discount =>
        filterObject(JSON.parse(JSON.stringify(discount)), '__typename'),
      ),
      standardFeatures: standardFeaturesTransformer(carSpecifications),
      model: {
        id: dealData.car.model?.attributes?.oemcode,
        make: selectedCar.car.trim?.attributes?.brand,
        title: dealData.car?.nameplate?.attributes?.shortname,
        description: dealData.car.model?.attributes?.longdescription,
        images: null,
        prices: null,
      },
      specPack: {
        id: dealData.car?.specPack?.attributes?.oemcode,
        title: dealData.car?.specPack?.attributes?.shortname,
        specs: {
          gvr: dealData.car?.specPack?.attributes?.gvr,
          tareweight: dealData.car?.specPack?.attributes?.tareweight,
          nviccode: dealData.car?.specPack?.attributes?.nviccode,
        },
      },
      engine: {
        title: selectedCar.car?.attributes?.specDescription?.[0],
      },
      gearbox: {
        title: selectedCar.car.attributes?.specDescription?.[1],
      },
      configurationSourceUrl: window.location.toString(),
      disclaimers: generalDisclaimerTransformer(dealData.car?.disclaimers),
      keyFeatures: keyFeaturesTransformer(selectedCar.car?.keyFeatures),
      exteriorColour: {
        id: null,
        description: null,
        title: dealData.car?.exterior?.shortname,
        images: imageTransformer(dealData.car?.images, ['front', 'back']),
      } as any,
      interiorColour: {
        id: null,
        description: null,
        title: dealData.car?.interior?.shortname,
        images: imageTransformer(dealData.car?.images, ['interior']),
      } as any,
      interiorImage: null,
      prices: mainPricesTransformer(dealData.car?.prices),
      options: optionsTransformer(dealData.car?.selectedFeatures),
    };
  },
};

export default carConfigurationService;
